import { useCallback, useState } from 'react';

import { useLingui } from '@lingui/react/macro';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';

import { parseErrorMessageFromPointerDE } from '../../../../helpers/parseErrorMessageFromPointer/parseErrorMessageFromPointerDE';
import logger from '../../../../utils/logger';
import { useMobileSignupNavContext } from '../../hooks/useMobileSignupNavContext';
import { TMobileSignupForm } from '../form';

type TFieldName = keyof TMobileSignupForm;

// Validates the whole-flow form store against one screen's schema on CTA tap.
// The provider store has no resolver (it spans every screen), so each screen
// validates its own subset here rather than owning a second useForm. When a
// screen passes an async onValid (a persistence PUT/POST), it runs with a
// loading flag and surfaces a generic inline error on failure without advancing.
export const useScreenValidation = (
  schema: yup.AnyObjectSchema,
  onValid?: () => void | Promise<void>,
) => {
  const { t } = useLingui();
  const { getValues, setError, clearErrors, setFocus } =
    useFormContext<TMobileSignupForm>();
  const { goNext } = useMobileSignupNavContext();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validateAndContinue = useCallback(async () => {
    clearErrors();
    setSubmitError(null);

    try {
      await schema.validate(getValues(), { abortEarly: false });
    } catch (error) {
      if (!(error instanceof yup.ValidationError)) throw error;

      error.inner.forEach((fieldError) => {
        if (!fieldError.path) return;
        setError(fieldError.path as TFieldName, {
          type: fieldError.type ?? 'validate',
          message: fieldError.message,
        });
      });

      const firstPath = error.inner.find((entry) => entry.path)?.path;
      if (firstPath) setFocus(firstPath as TFieldName);
      return;
    }

    if (!onValid) {
      goNext();
      return;
    }

    setIsSubmitting(true);
    try {
      await onValid();
      goNext();
    } catch (error) {
      // parseErrorMessageFromPointerDE JSON-parses error.message, so guard
      // against non-JSON messages (e.g. network errors) throwing — otherwise
      // the generic failure path below never runs.
      let field: ReturnType<typeof parseErrorMessageFromPointerDE> = 'other';
      if (error instanceof Error) {
        try {
          field = parseErrorMessageFromPointerDE(error);
        } catch {
          field = 'other';
        }
      }

      setSubmitError(
        field !== 'other'
          ? `${t`Please double check the details entered for:`} “${field}”`
          : t`Something went wrong.`,
      );
      logger.error('MobileSignup: screen persistence failed', {
        tags: { team: 'de-introducer' },
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [t, schema, getValues, setError, clearErrors, setFocus, goNext, onValid]);

  return { validateAndContinue, isSubmitting, submitError };
};
